import { Link, useParams } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import { Eye, EyeOff, Trash2, User } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useDeleteComment, useUpdateComment } from '@/api/comments/queries';
import type { Comment } from '@/api/comments/types';
import { useGetSubscription } from '@/api/subscription/queries';
import { ConfirmDeleteAlert } from '@/components/confirm-delete-alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { UserAvatar } from '@/components/user-avatar';
import { Route } from '..';

type Props = {
  comment: Comment;
  isAuthor: boolean;
  isMock?: boolean;
};

export const RequestComment = ({ comment, isAuthor, isMock }: Props) => {
  const { data: subscription } = useGetSubscription();
  const deleteMutation = useDeleteComment();
  const updateMutation = useUpdateComment();
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const search = Route.useSearch();
  const [commentToRemove, setCommentToRemove] = useState<Comment | null>(null);

  const handleDeleteComment = (confirmed: boolean) => {
    if (!confirmed) {
      return setCommentToRemove(null);
    }

    if (!commentToRemove) {
      return;
    }

    setCommentToRemove(null);
    deleteMutation.mutateAsync(
      {
        projectId: projectId,
        requestId: commentToRemove.requestId,
        commentId: commentToRemove.id,
        status: search,
      },
      { onSuccess: () => toast.success('Comment had been removed') },
    );
  };

  const handleToggleVisibility = () => {
    updateMutation.mutateAsync(
      {
        projectId: projectId,
        requestId: comment.requestId,
        commentId: comment.id,
        status: search,
        data: { visible: !comment.visible },
      },
      {
        onSuccess: () => toast.success(`Comment ${comment.visible ? 'hidden' : 'shown'}`),
        onError: () => toast.error('Failed to update comment visibility'),
      },
    );
  };

  return (
    <div className="flex gap-x-2.5" key={comment.id}>
      <UserAvatar userId={comment.userId} />
      <div className={`flex-1 space-y-0.5 text-sm ${isMock ? 'select-none' : ''}`}>
        <span className="text-xs text-muted-foreground">
          <User className="mr-0.5 inline" size={12} />
          <Link
            className={isMock ? 'select-none' : ''}
            to="/projects/$projectId/users/$userId"
            params={{ projectId, userId: comment.userId }}
            disabled={isMock}
          >
            {comment.user?.email || comment.userId}{' '}
            {isAuthor ? <Badge variant="secondary">Author</Badge> : null}
          </Link>
        </span>
        <div>{comment.text}</div>
        <div className="inline-flex items-center space-x-2 text-xs text-muted-foreground/60">
          <span>{formatDistanceToNow(comment.createdAt)} ago</span>
          {!subscription?.isFree && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className="shrink-0 gap-1 text-xs hover:bg-transparent"
                    variant="ghost"
                    size="sm"
                    onClick={handleToggleVisibility}
                    disabled={updateMutation.isPending}
                  >
                    {!comment.visible ? <EyeOff size={14} /> : <Eye size={14} />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{comment.visible ? 'Hide comment' : 'Show comment'}</TooltipContent>
              </Tooltip>
              <Button
                className="shrink-0 gap-1 text-xs text-red-600 dark:text-red-400 hover:bg-transparent"
                variant="ghost"
                size="sm"
                onClick={() => setCommentToRemove(comment)}
              >
                <Trash2 size={12} />
                Remove
              </Button>
            </>
          )}
        </div>
      </div>
      <ConfirmDeleteAlert
        title="Delete Comment"
        description="This action cannot be undone. This will permanently delete this comment."
        isOpen={!!commentToRemove}
        onAction={handleDeleteComment}
      />
    </div>
  );
};
